import React, { useState } from "react";

import type { SxProps } from "@mui/material";

import { Row } from "@rui/Stack";

import { SidebarCollapse, SidebarContent, SidebarToggleButton } from "./styles";

interface BaseCollapsibleSidebarProps {
  backgroundColor?: React.CSSProperties["backgroundColor"];
  children: React.ReactNode;
  overlay?: React.ReactNode;
  sx?: SxProps;
}

interface ExpandProps {
  expanded: boolean;
  onToggleExpand?: (expand: boolean) => void;
}

type ControlledCollapsibleSidebarProps = ExpandProps &
  BaseCollapsibleSidebarProps;
type CollapsibleSidebarProps = Partial<ExpandProps> &
  BaseCollapsibleSidebarProps;

const ControlledCollapsibleSidebar = ({
  backgroundColor,
  children,
  expanded,
  onToggleExpand,
  overlay,
  sx,
}: ControlledCollapsibleSidebarProps) => {
  const handleToggleExpanded = () => onToggleExpand?.(!expanded);
  return (
    <Row position="relative">
      <SidebarCollapse
        backgroundColor={backgroundColor}
        orientation="horizontal"
        in={expanded}
        collapsedSize="16px"
        onClick={expanded ? undefined : handleToggleExpanded}
      >
        <SidebarToggleButton
          expanded={expanded}
          onClick={handleToggleExpanded}
        />
        {overlay}
        <SidebarContent sx={sx}>{children}</SidebarContent>
      </SidebarCollapse>
    </Row>
  );
};

const UncontrolledCollapsibleSidebar = (props: CollapsibleSidebarProps) => {
  const [expanded, setExpanded] = useState(true);
  return (
    <ControlledCollapsibleSidebar
      {...props}
      expanded={expanded}
      onToggleExpand={setExpanded}
    />
  );
};

const CollapsibleSidebar = ({
  expanded,
  ...props
}: CollapsibleSidebarProps) => {
  if (expanded != null) {
    return <ControlledCollapsibleSidebar expanded={expanded} {...props} />;
  }
  return <UncontrolledCollapsibleSidebar {...props} />;
};

export default CollapsibleSidebar;
